import { useState, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://practice-api.ai.dentist';

// ── Fonts injected via index.html (Syne + DM Sans) ──────────────────────

export default function App() {
  const [stage, setStage]         = useState('upload');   // upload | loading | results | thankyou
  const [dragging, setDragging]   = useState(false);
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [results, setResults]     = useState(null);
  const [error, setError]         = useState(null);
  const [email, setEmail]         = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f || !f.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, TIFF)');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  }, []);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const identify = async () => {
    if (!file) return;
    setStage('loading');
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_URL}/api/implants/identify`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }
      const data = await res.json();
      // If no matches found, treat as a soft error with a helpful message
      if (!data.matches || data.matches.length === 0) {
        setError('No implant matches found. Make sure the X-ray clearly shows the implant body. Crowns, bridges, and natural teeth will not match.');
        setStage('upload');
        return;
      }
      setResults(data);
      setStage('results');
    } catch (e) {
      if (e.message === 'Failed to fetch' || e.name === 'TypeError') {
        setError('Could not reach the server. Please check your connection and try again.');
      } else if (e.message.includes('503')) {
        setError('The identification model is warming up — please try again in 30 seconds.');
      } else if (e.message.includes('400')) {
        setError('This file doesn\'t look like a valid X-ray image. Please upload a JPEG or PNG.');
      } else {
        setError(e.message || 'Something went wrong. Please try again.');
      }
      setStage('upload');
    }
  };

  const submitEmail = async () => {
    if (!email || !email.includes('@')) return;
    setEmailLoading(true);
    try {
      await fetch(`${API_URL}/api/implants/save-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'implant_tool',
          results: results?.matches?.map(m => m.implant_name),
        }),
      });
    } catch (e) {
      // silent — don't block the user if this fails
    }
    setEmailLoading(false);
    setEmailSent(true);
    setStage('thankyou');
  };

  const reset = () => {
    setStage('upload');
    setFile(null);
    setPreview(null);
    setResults(null);
    setError(null);
    setEmail('');
    setEmailSent(false);
  };

  return (
    <div style={s.page}>
      {/* Background grid */}
      <div style={s.grid} aria-hidden="true" />
      <div style={s.glow1} aria-hidden="true" />
      <div style={s.glow2} aria-hidden="true" />

      {/* Nav */}
      <nav style={s.nav}>
        <div style={s.navInner}>
          <div style={s.logo}>
            <span style={s.logoIcon}>⬡</span>
            <span style={s.logoText}>AI DENTIST</span>
          </div>
          <a href="https://intake.dental" style={s.navCta} target="_blank" rel="noopener noreferrer">
            Intake.Dental →
          </a>
        </div>
      </nav>

      {/* Hero */}
      <header style={s.hero}>
        <div style={s.heroTag}>FREE TOOL · NO ACCOUNT NEEDED</div>
        <h1 style={s.heroTitle}>
          Identify Any<br />
          <span style={s.heroAccent}>Dental Implant</span><br />
          Instantly
        </h1>
        <p style={s.heroSub}>
          Upload a periapical X-ray. Our AI reads the morphological features
          and matches against our FDA-cleared implant database.
        </p>
      </header>

      {/* Main card */}
      <main style={s.main}>
        {stage === 'upload' && (
          <div style={s.card}>
            <div style={s.cardLabel}>UPLOAD X-RAY</div>

            {/* Drop zone */}
            <div
              style={{ ...s.dropzone, ...(dragging ? s.dropzoneDrag : {}), ...(preview ? s.dropzoneHasFile : {}) }}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => fileRef.current.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              {preview ? (
                <div style={s.previewWrap}>
                  <img src={preview} alt="X-ray preview" style={s.previewImg} />
                  <div style={s.previewName}>{file?.name}</div>
                  <div style={s.previewChange}>Click to change</div>
                </div>
              ) : (
                <div style={s.dropContent}>
                  <div style={s.dropIcon}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                    </svg>
                  </div>
                  <div style={s.dropText}>Drop X-ray here</div>
                  <div style={s.dropSub}>or click to browse · JPG, PNG, TIFF</div>
                </div>
              )}
            </div>

            {error && <div style={s.errorMsg}>{error}</div>}

            <button
              style={{ ...s.btn, ...(!file ? s.btnDisabled : {}) }}
              onClick={identify}
              disabled={!file}
            >
              Identify Implant
              <span style={s.btnArrow}>→</span>
            </button>

            <div style={s.disclaimer}>
              For clinical reference only. Not a substitute for professional diagnosis.
            </div>
          </div>
        )}

        {stage === 'loading' && (
          <div style={s.card}>
            <div style={s.loadingWrap}>
              <div style={s.spinner} />
              <div style={s.loadingTitle}>Analyzing X-ray</div>
              <div style={s.loadingSteps}>
                <LoadingStep text="Reading morphological features" delay={0} />
                <LoadingStep text="Matching against implant database" delay={800} />
                <LoadingStep text="Ranking top candidates" delay={1600} />
              </div>
            </div>
          </div>
        )}

        {stage === 'results' && results && (
          <div style={s.card}>
            <div style={s.resultsHeader}>
              <div style={s.cardLabel}>IDENTIFICATION RESULTS</div>
              <button style={s.resetBtn} onClick={reset}>↩ New X-ray</button>
            </div>

            <div style={s.matchList}>
              {(results.matches || []).length === 0 && (
                <div style={s.noMatch}>
                  No strong matches found. The implant may not be in our database yet.
                </div>
              )}
              {(results.matches || []).map((m, i) => (
                <MatchCard key={i} match={m} rank={i + 1} />
              ))}
            </div>

            {/* Email capture */}
            <div style={s.emailBox}>
              <div style={s.emailTitle}>Email these results to yourself</div>
              <div style={s.emailRow}>
                <input
                  style={s.emailInput}
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitEmail()}
                />
                <button
                  style={s.emailBtn}
                  onClick={submitEmail}
                  disabled={emailLoading}
                >
                  {emailLoading ? '...' : 'Send'}
                </button>
              </div>
              <div style={s.emailNote}>No spam. One email with your results.</div>
            </div>

            <div style={s.disclaimer}>
              For clinical reference only. Always verify with manufacturer documentation.
            </div>
          </div>
        )}

        {stage === 'thankyou' && (
          <div style={s.card}>
            <div style={s.tyWrap}>
              <div style={s.tyCheck}>✓</div>
              <div style={s.tyTitle}>Results sent!</div>
              <div style={s.tySub}>Check your inbox at {email}</div>

              <div style={s.tyDivider} />

              {/* Intake.Dental CTA */}
              <div style={s.intakeBox}>
                <div style={s.intakeTag}>MADE BY THE SAME DENTIST</div>
                <div style={s.intakeTitle}>Run a dental practice?</div>
                <div style={s.intakeSub}>
                  Intake.Dental is the AI-powered patient intake platform built by a
                  practicing DMD — digital forms, insurance verification, clinical notes,
                  and more. No IT required.
                </div>
                <a href="https://intake.dental" style={s.intakeBtn} target="_blank" rel="noopener noreferrer">
                  Try Intake.Dental Free for 30 Days →
                </a>
                <div style={s.intakeFeatures}>
                  <span>✓ HIPAA compliant</span>
                  <span>✓ Setup in 10 minutes</span>
                  <span>✓ No contracts</span>
                </div>
              </div>

              <button style={s.resetBtn} onClick={reset}>Identify another implant</button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={s.footer}>
        <span>Built by <a href="https://intake.dental" style={s.footerLink}>Intake.Dental</a></span>
        <span style={s.footerDot}>·</span>
        <span>Free forever</span>
        <span style={s.footerDot}>·</span>
        <span>For clinical reference only</span>
      </footer>
    </div>
  );
}

function LoadingStep({ text, delay }) {
  const [visible, setVisible] = useState(delay === 0);
  if (delay > 0) setTimeout(() => setVisible(true), delay);
  return (
    <div style={{ ...s.loadingStep, opacity: visible ? 1 : 0, transition: 'opacity 0.4s' }}>
      <span style={s.loadingDot} />
      {text}
    </div>
  );
}

function MatchCard({ match, rank }) {
  const pct = Math.round((match.confidence || 0) * 100);
  const rankColors = ['#a78bfa', '#60a5fa', '#34d399'];
  const color = rankColors[rank - 1] || '#94a3b8';

  return (
    <div style={{ ...s.matchCard, borderColor: rank === 1 ? color + '40' : '#1e2530' }}>
      <div style={s.matchRank}>
        <div style={{ ...s.matchRankNum, color }}># {rank}</div>
        <div style={{ ...s.matchConf, color }}>
          <div style={{ ...s.confBar, width: `${pct}%`, background: color }} />
          {pct}% match
        </div>
      </div>
      <div style={s.matchInfo}>
        <div style={s.matchName}>{match.implant_name || 'Unknown'}</div>
        <div style={s.matchMfr}>{match.manufacturer || '—'}</div>
        {match.metadata && Object.keys(match.metadata).length > 0 && (
          <div style={s.matchTags}>
            {Object.entries(match.metadata).slice(0, 4).map(([k, v]) => v && (
              <span key={k} style={s.matchTag}>{k.replace(/_/g, ' ')}: {v}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    background: '#080b12',
    color: '#e2e8f0',
    fontFamily: "'DM Sans', sans-serif",
    position: 'relative',
    overflow: 'hidden',
  },
  grid: {
    position: 'fixed', inset: 0, zIndex: 0,
    backgroundImage: `
      linear-gradient(rgba(99,102,241,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(99,102,241,0.03) 1px, transparent 1px)
    `,
    backgroundSize: '48px 48px',
    pointerEvents: 'none',
  },
  glow1: {
    position: 'fixed', top: '-20%', left: '-10%',
    width: '60vw', height: '60vw', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(109,40,217,0.12) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 0,
  },
  glow2: {
    position: 'fixed', bottom: '-20%', right: '-10%',
    width: '50vw', height: '50vw', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(59,130,246,0.10) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 0,
  },
  nav: {
    position: 'relative', zIndex: 10,
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    backdropFilter: 'blur(12px)',
  },
  navInner: {
    maxWidth: 900, margin: '0 auto', padding: '16px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  logo: { display: 'flex', alignItems: 'center', gap: 10 },
  logoIcon: { fontSize: 22, color: '#818cf8' },
  logoText: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 15, fontWeight: 800, letterSpacing: '0.15em',
    color: '#f1f5f9',
  },
  navCta: {
    fontSize: 13, color: '#818cf8', textDecoration: 'none',
    fontWeight: 500, letterSpacing: '0.02em',
    transition: 'color 0.2s',
  },
  hero: {
    position: 'relative', zIndex: 1,
    maxWidth: 900, margin: '0 auto',
    padding: '72px 24px 40px',
    textAlign: 'center',
  },
  heroTag: {
    display: 'inline-block',
    fontSize: 11, fontWeight: 600, letterSpacing: '0.12em',
    color: '#818cf8', marginBottom: 24,
    padding: '5px 14px', borderRadius: 20,
    border: '1px solid rgba(129,140,248,0.3)',
    background: 'rgba(129,140,248,0.08)',
  },
  heroTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 'clamp(40px, 7vw, 72px)',
    fontWeight: 800, lineHeight: 1.05,
    color: '#f1f5f9', margin: '0 0 20px',
    letterSpacing: '-0.03em',
  },
  heroAccent: {
    background: 'linear-gradient(135deg, #818cf8, #60a5fa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  heroSub: {
    fontSize: 17, color: '#64748b', lineHeight: 1.6,
    maxWidth: 520, margin: '0 auto',
  },
  main: {
    position: 'relative', zIndex: 1,
    maxWidth: 600, margin: '0 auto',
    padding: '0 24px 80px',
  },
  card: {
    background: 'rgba(15,20,30,0.8)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 20,
    padding: '32px',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
  },
  cardLabel: {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.15em',
    color: '#475569', marginBottom: 20,
    fontFamily: "'DM Mono', monospace",
  },
  dropzone: {
    border: '1.5px dashed rgba(255,255,255,0.1)',
    borderRadius: 12, padding: '40px 24px',
    cursor: 'pointer', textAlign: 'center',
    transition: 'all 0.2s',
    marginBottom: 20,
    background: 'rgba(255,255,255,0.02)',
  },
  dropzoneDrag: {
    borderColor: '#818cf8',
    background: 'rgba(129,140,248,0.05)',
  },
  dropzoneHasFile: {
    borderColor: 'rgba(129,140,248,0.3)',
    borderStyle: 'solid',
  },
  dropContent: {},
  dropIcon: { color: '#334155', marginBottom: 12 },
  dropText: { fontSize: 16, fontWeight: 600, color: '#94a3b8', marginBottom: 4 },
  dropSub:  { fontSize: 12, color: '#475569' },
  previewWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  previewImg: {
    maxHeight: 200, maxWidth: '100%', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.08)',
    filter: 'brightness(0.9)',
  },
  previewName:   { fontSize: 12, color: '#64748b' },
  previewChange: { fontSize: 11, color: '#475569' },
  errorMsg: {
    fontSize: 13, color: '#f87171',
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: 8, padding: '10px 14px', marginBottom: 16,
  },
  btn: {
    width: '100%', padding: '16px 24px',
    background: 'linear-gradient(135deg, #6d28d9, #3b82f6)',
    border: 'none', borderRadius: 12,
    color: '#fff', fontSize: 15, fontWeight: 600,
    cursor: 'pointer', display: 'flex',
    justifyContent: 'center', alignItems: 'center', gap: 8,
    letterSpacing: '0.01em',
    transition: 'opacity 0.2s, transform 0.1s',
  },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  btnArrow: { fontSize: 18 },
  disclaimer: {
    fontSize: 11, color: '#334155', textAlign: 'center',
    marginTop: 16, lineHeight: 1.5,
  },

  // Loading
  loadingWrap: { textAlign: 'center', padding: '20px 0' },
  spinner: {
    width: 44, height: 44, borderRadius: '50%',
    border: '3px solid rgba(129,140,248,0.15)',
    borderTop: '3px solid #818cf8',
    margin: '0 auto 24px',
    animation: 'spin 0.8s linear infinite',
  },
  loadingTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 20,
  },
  loadingSteps: { display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start', display: 'inline-flex' },
  loadingStep: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#64748b' },
  loadingDot: {
    width: 6, height: 6, borderRadius: '50%',
    background: '#818cf8', flexShrink: 0,
  },

  // Results
  resultsHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20,
  },
  resetBtn: {
    fontSize: 12, color: '#64748b', background: 'none', border: 'none',
    cursor: 'pointer', padding: '4px 8px',
  },
  matchList: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28 },
  noMatch: { fontSize: 14, color: '#475569', textAlign: 'center', padding: '20px 0' },
  matchCard: {
    border: '1px solid #1e2530', borderRadius: 12,
    padding: '16px 18px', display: 'flex', gap: 16,
    background: 'rgba(255,255,255,0.02)',
  },
  matchRank: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', minWidth: 60 },
  matchRankNum: { fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700 },
  matchConf: { fontSize: 11, fontFamily: "'DM Mono', monospace", textAlign: 'center' },
  confBar: { height: 3, borderRadius: 2, marginBottom: 3, transition: 'width 0.6s ease' },
  matchInfo: { flex: 1 },
  matchName: { fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 2 },
  matchMfr:  { fontSize: 12, color: '#64748b', marginBottom: 8 },
  matchTags: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  matchTag:  {
    fontSize: 10, color: '#475569',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 4, padding: '2px 8px',
    fontFamily: "'DM Mono', monospace",
  },

  // Email
  emailBox: {
    background: 'rgba(129,140,248,0.05)',
    border: '1px solid rgba(129,140,248,0.15)',
    borderRadius: 12, padding: '20px',
    marginBottom: 20,
  },
  emailTitle: { fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 12 },
  emailRow:   { display: 'flex', gap: 8 },
  emailInput: {
    flex: 1, padding: '10px 14px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, color: '#f1f5f9', fontSize: 13,
    outline: 'none',
  },
  emailBtn: {
    padding: '10px 18px',
    background: 'linear-gradient(135deg, #6d28d9, #3b82f6)',
    border: 'none', borderRadius: 8,
    color: '#fff', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
  emailNote: { fontSize: 11, color: '#334155', marginTop: 8 },

  // Thank you
  tyWrap: { textAlign: 'center' },
  tyCheck: {
    fontSize: 40, marginBottom: 12,
    background: 'linear-gradient(135deg, #818cf8, #60a5fa)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  tyTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 26, fontWeight: 800, color: '#f1f5f9', marginBottom: 4,
  },
  tySub: { fontSize: 14, color: '#64748b', marginBottom: 28 },
  tyDivider: { height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 28 },

  // Intake CTA
  intakeBox: {
    background: 'linear-gradient(135deg, rgba(109,40,217,0.1), rgba(59,130,246,0.08))',
    border: '1px solid rgba(129,140,248,0.2)',
    borderRadius: 14, padding: '24px', marginBottom: 24, textAlign: 'left',
  },
  intakeTag: {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
    color: '#818cf8', marginBottom: 8,
  },
  intakeTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 20, fontWeight: 800, color: '#f1f5f9', marginBottom: 8,
  },
  intakeSub: { fontSize: 13, color: '#64748b', lineHeight: 1.6, marginBottom: 18 },
  intakeBtn: {
    display: 'block', textAlign: 'center',
    padding: '13px 24px',
    background: 'linear-gradient(135deg, #6d28d9, #3b82f6)',
    borderRadius: 10, color: '#fff',
    fontSize: 13, fontWeight: 600, textDecoration: 'none',
    marginBottom: 14,
  },
  intakeFeatures: {
    display: 'flex', gap: 16, justifyContent: 'center',
    fontSize: 11, color: '#475569',
  },

  footer: {
    position: 'relative', zIndex: 1,
    textAlign: 'center', padding: '24px',
    fontSize: 12, color: '#334155',
    borderTop: '1px solid rgba(255,255,255,0.04)',
    display: 'flex', justifyContent: 'center',
    alignItems: 'center', gap: 8,
  },
  footerLink: { color: '#64748b', textDecoration: 'none' },
  footerDot:  { opacity: 0.4 },
};